import { IconCheck, IconCircleCheck, IconPencil, IconRefresh, IconX } from '@tabler/icons-react-native';
import { SwipeableReviewCard, type SwipeableReviewCardHandle } from 'app/components/swipeable-review-card';
import { noOutsetShadow } from 'app/design/outset-shadow';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Input } from 'app/design/primitives/input';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import {
  getBulkDraftDescription,
  getBulkDraftTitle,
  type BulkListingDraft,
  type BulkListingSession,
} from 'app/network/listing';
import { createTheme } from 'app/shopify';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Keyboard, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Animated, { FadeIn, useReducedMotion } from 'react-native-reanimated';

const theme = createTheme();

export function BulkReviewStep({
  currentDraft,
  isBusy,
  onComplete,
  onRetry,
  onSave,
  onSkip,
  progress,
}: {
  currentDraft?: BulkListingDraft;
  isBusy: boolean;
  onComplete: () => void;
  onRetry: (draftId: string) => void;
  onSave: (draftId: string, title: string, description: string) => void;
  onSkip: (draftId: string) => void;
  progress?: BulkListingSession['progress'];
}) {
  const { t } = useTranslation('new');
  const swipeCard = useRef<SwipeableReviewCardHandle>(null);
  const reducedMotion = useReducedMotion();
  const { isEditing, title, description, failed, saveFailed, canSave, update, setEditingId } =
    useBulkDraftEdits(currentDraft);
  const accept = () => {
    if (currentDraft && canSave && !isBusy) onSave(currentDraft.id, title.trim(), description.trim());
  };
  const reject = () => {
    if (currentDraft && !isBusy) onSkip(currentDraft.id);
  };

  if (!currentDraft)
    return (
      <View style={styles.empty}>
        {isBusy ? (
          <ActivityIndicator size="large" color={theme.colors['surface.brand.default']} />
        ) : (
          <Column gap="spacing.general.x4" alignItems="center">
            <IconCircleCheck size={48} color={theme.colors['surface.brand.default']} />
            <Text variant="headerXSmall" fontWeight="700" textAlign="center">
              {t('bulk.review.noDraftsTitle')}
            </Text>
            <Button onPress={onComplete} variant="primary">
              {t('bulk.review.finish')}
            </Button>
          </Column>
        )}
      </View>
    );

  const reviewed = (progress?.saved ?? 0) + (progress?.skipped ?? 0);
  const total = progress?.total ?? 1;
  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <Row alignItems="center" justifyContent="space-between" gap="spacing.general.x3">
          <Text color="text.primary" fontWeight="700" variant="bodyLarge">
            {t(isEditing ? 'bulk.review.editTitle' : 'bulk.review.title')}
          </Text>
          <View style={styles.counter}>
            <Text color="text.secondary" fontWeight="600" variant="bodySmall" accessibilityLiveRegion="polite">
              {t('bulk.review.position', { current: Math.min(reviewed + 1, total), total })}
            </Text>
          </View>
        </Row>
        <View
          style={styles.track}
          accessibilityRole="progressbar"
          accessibilityValue={{ min: 0, max: total, now: reviewed }}
          accessibilityLabel={t('bulk.review.title')}
        >
          <View style={[styles.fill, { width: `${String(total ? (reviewed / total) * 100 : 0)}%` as `${number}%` }]} />
        </View>
      </View>
      {isEditing ? (
        <Page
          stretchToFill
          keyboardShouldPersistTaps="handled"
          paddingStyle={{ padding: 24 }}
          accessory={
            <View style={styles.dock}>
              <Button
                variant="primary"
                style={styles.primary}
                onPress={() => {
                  Keyboard.dismiss();
                  setEditingId(null);
                }}
              >
                {t('bulk.review.doneEditing')}
              </Button>
            </View>
          }
        >
          <Column gap="spacing.general.x5">
            <Row alignItems="center" gap="spacing.general.x4">
              <View style={styles.editPhoto}>
                <PCImage source={{ uri: currentDraft.imageUrl }} contentFit="cover" style={StyleSheet.absoluteFill} />
              </View>
              <Text color="text.secondary" variant="bodySmall" style={{ flex: 1 }}>
                {t('bulk.review.editHint')}
              </Text>
            </Row>
            <Input
              label={t('itemInformationSection.nameFieldLabel')}
              markAsRequired
              onChangeText={(value) => update({ title: value })}
              value={title}
            />
            {title.trim().length < 3 ? (
              <Text color="text.error" variant="bodySmall">
                {t('bulk.review.titleHint')}
              </Text>
            ) : null}
            <Input
              label={t('itemInformationSection.descriptionFieldLabel')}
              multiline
              onChangeText={(value) => update({ description: value })}
              scrollEnabled={false}
              style={{ minHeight: 160 }}
              value={description}
            />
          </Column>
        </Page>
      ) : (
        <ScrollView
          style={styles.cardArea}
          contentContainerStyle={styles.cardAreaContent}
          showsVerticalScrollIndicator={false}
        >
          <Animated.View
            key={currentDraft.id}
            entering={reducedMotion ? undefined : FadeIn.duration(180)}
            style={styles.cardSlot}
          >
            <SwipeableReviewCard
              ref={swipeCard}
              resetKey={currentDraft.id}
              acceptLabel={t('bulk.review.acceptSwipe')}
              rejectLabel={t('bulk.review.rejectSwipe')}
              canAccept={canSave}
              disabled={isBusy}
              onAccept={accept}
              onReject={reject}
              overlayVariant="gradient"
              maxRotation={6}
              verticalDragFactor={0.08}
              style={styles.card}
            >
              <View style={styles.photo}>
                <PCImage
                  source={{ uri: currentDraft.imageUrl }}
                  contentFit="cover"
                  contentPosition="center"
                  style={StyleSheet.absoluteFill}
                />
              </View>
              <BulkDraftCopy
                failed={failed}
                saveFailed={saveFailed}
                title={title}
                description={description}
                isBusy={isBusy}
                onEdit={() => setEditingId(currentDraft.id)}
              />
            </SwipeableReviewCard>
          </Animated.View>
        </ScrollView>
      )}
      {!isEditing ? (
        <BulkReviewActions
          isBusy={isBusy}
          failed={failed}
          canSave={canSave}
          swipeCard={swipeCard}
          onRetry={onRetry}
          draftId={currentDraft.id}
        />
      ) : null}
    </View>
  );
}

function BulkReviewActions({
  isBusy,
  failed,
  canSave,
  swipeCard,
  onRetry,
  draftId,
}: {
  isBusy: boolean;
  failed: boolean;
  canSave: boolean;
  swipeCard: React.RefObject<SwipeableReviewCardHandle | null>;
  onRetry: (id: string) => void;
  draftId: string;
}) {
  const { t } = useTranslation('new');
  return (
    <View style={styles.dock}>
      <Text color="text.secondary" textAlign="center" variant="bodySmall">
        {t('bulk.review.swipeHint')}
      </Text>
      <Row gap="spacing.general.x3">
        <Button
          disabled={isBusy}
          onPress={() => swipeCard.current?.swipe('reject')}
          style={styles.action}
          variant="secondary"
        >
          <IconX size={24} color={theme.colors['text.primary']} />
          <Text marginLeft="spacing.general.x2" fontWeight="600">
            {t('bulk.review.reject')}
          </Text>
        </Button>
        <Button
          disabled={isBusy || (!failed && !canSave)}
          onPress={() => (failed ? onRetry(draftId) : swipeCard.current?.swipe('accept'))}
          style={[styles.action, styles.primary]}
          variant="primary"
        >
          {failed ? (
            <IconRefresh size={24} color={theme.colors['surface.default']} />
          ) : (
            <IconCheck size={24} color={theme.colors['surface.default']} />
          )}
          <Text color="surface.default" fontWeight="600" marginLeft="spacing.general.x2">
            {t(failed ? 'bulk.review.retry' : 'bulk.review.accept')}
          </Text>
        </Button>
      </Row>
    </View>
  );
}

function useBulkDraftEdits(currentDraft: BulkListingDraft | undefined) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, { title: string; description: string }>>({});
  const isEditing = currentDraft?.id === editingId;
  const currentEdits = currentDraft ? edits[currentDraft.id] : undefined;
  const title = currentEdits?.title ?? (currentDraft ? getBulkDraftTitle(currentDraft) : '');
  const description = currentEdits?.description ?? (currentDraft ? getBulkDraftDescription(currentDraft) : '');
  const failed = currentDraft?.status === 'generation_failed';
  const saveFailed = currentDraft?.status === 'save_failed';
  const canSave = title.trim().length >= 3 && !failed;
  const update = (next: Partial<{ title: string; description: string }>) => {
    if (!currentDraft) return;
    setEdits((previous) => ({ ...previous, [currentDraft.id]: { title, description, ...next } }));
  };
  return { isEditing, title, description, failed, saveFailed, canSave, update, setEditingId };
}

function BulkDraftCopy({
  failed,
  saveFailed,
  title,
  description,
  isBusy,
  onEdit,
}: {
  failed: boolean;
  saveFailed: boolean;
  title: string;
  description: string;
  isBusy: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('new');
  return (
    <View style={styles.copy}>
      {failed || saveFailed ? (
        <View style={styles.notice}>
          <Text fontWeight="600" variant="bodyBase">
            {t(failed ? 'bulk.review.generationFailedTitle' : 'bulk.review.saveFailedTitle')}
          </Text>
          <Text color="text.secondary" variant="bodySmall">
            {t(failed ? 'bulk.review.generationFailedDescription' : 'bulk.review.saveFailedDescription')}
          </Text>
        </View>
      ) : null}
      {!failed ? (
        <>
          <Text color="text.primary" fontWeight="700" variant="headerXSmall" numberOfLines={2}>
            {title || t('bulk.review.untitled')}
          </Text>
          {description ? (
            <Text color="text.secondary" variant="bodyBase" numberOfLines={3}>
              {description}
            </Text>
          ) : null}
          <Pressable accessibilityRole="button" onPress={onEdit} disabled={isBusy} style={styles.edit}>
            <IconPencil size={16} color={theme.colors['text.primary']} />
            <Text color="text.primary" fontWeight="600" variant="bodySmall">
              {t('bulk.review.edit')}
            </Text>
          </Pressable>
        </>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  header: { paddingHorizontal: 24, paddingTop: 12, paddingBottom: 16, gap: 12 },
  counter: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: 100,
  },
  track: { height: 3, borderRadius: 2, overflow: 'hidden', backgroundColor: theme.colors['surface.tertiary-neutral'] },
  fill: { height: '100%', backgroundColor: theme.colors['surface.brand.default'] },
  cardArea: { flex: 1 },
  cardAreaContent: { flexGrow: 1, paddingHorizontal: 24, paddingBottom: 8 },
  cardSlot: { flex: 1, minHeight: 420 },
  card: {
    flex: 1,
    padding: 0,
    borderRadius: theme.borderRadii['radius.xl'],
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: theme.colors['border.secondary'],
    ...noOutsetShadow,
  },
  photo: { flex: 1, minHeight: 180, backgroundColor: theme.colors['surface.tertiary-neutral'] },
  copy: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 8, gap: 8 },
  edit: { flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 44, alignSelf: 'flex-start' },
  notice: {
    padding: 12,
    gap: 8,
    borderRadius: theme.borderRadii['radius.md'],
    backgroundColor: theme.colors['surface.warning.subtle'],
  },
  dock: { paddingHorizontal: 24, paddingTop: 12, paddingBottom: 8, gap: 12 },
  action: { flex: 1, minHeight: 56, borderRadius: theme.borderRadii['radius.lg'] },
  primary: {
    backgroundColor: theme.colors['surface.brand.default'],
    minHeight: 56,
    borderRadius: theme.borderRadii['radius.lg'],
  },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  editPhoto: { width: 88, height: 88, borderRadius: theme.borderRadii['radius.lg'], overflow: 'hidden' },
});
